import AppsIcon from '@mui/icons-material/Apps';
import BadgeIcon from '@mui/icons-material/Badge';
import BusinessIcon from '@mui/icons-material/Business';
import DashboardIcon from '@mui/icons-material/Dashboard';
import GavelIcon from '@mui/icons-material/Gavel';
import HomeIcon from '@mui/icons-material/Home';
import PeopleIcon from '@mui/icons-material/People';
import { alpha, Theme } from '@mui/material/styles';
import { permission } from '@nohara-dxdev/oidc-core';
import { BreadcrumbItem, DashboardLayout } from '@nohara-dxdev/react-oidc';
import { PropsWithChildren, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { env } from '@/config/env';
import { paths } from '@/config/paths';

const readAppsPermissions = [permission.app.read];
const readRolesPermissions = [permission.role.read];
const readUsersPermissions = [permission.user.read];
const readOrgsPermissions = [permission.org.read];
const readTermsPermissions = [permission.terms.read];

export const AdminLayout = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation();

  const sections = useMemo(
    () => [
      [
        {
          path: paths.admin.dashboard.getHref(),
          icon: <DashboardIcon />,
          label: t('navigation.menu.dashboard'),
        },
        {
          path: paths.admin.apps.getHref(),
          icon: <AppsIcon />,
          label: t('navigation.menu.apps'),
          requireAll: readAppsPermissions,
        },
        {
          path: paths.admin.roles.getHref(),
          icon: <BadgeIcon />,
          label: t('navigation.menu.roles'),
          requireAll: readRolesPermissions,
        },
        {
          path: paths.admin.users.getHref(),
          icon: <PeopleIcon />,
          label: t('navigation.menu.users'),
          requireAll: readUsersPermissions,
        },
        {
          path: paths.admin.orgs.getHref(),
          icon: <BusinessIcon />,
          label: t('navigation.menu.organizations'),
          requireAll: readOrgsPermissions,
        },
        {
          path: paths.admin.terms.getHref(),
          icon: <GavelIcon />,
          label: t('navigation.menu.terms'),
          requireAll: readTermsPermissions,
        },
      ],
    ],
    [t],
  );

  const bottomSections = useMemo(
    () => [
      [
        {
          path: paths.org.root.getHref(),
          icon: <HomeIcon />,
          label: t('navigation.menu.home'),
        },
      ],
    ],
    [t],
  );

  const breadcrumbs: BreadcrumbItem[] = useMemo(
    () => [{ label: t('navigation.menu.adminPanel'), href: paths.admin.root.path }],
    [t],
  );

  return (
    <DashboardLayout
      sections={sections}
      bottomSections={bottomSections}
      navTitle={t('navigation.adminTitle')}
      pageTitle={t('navigation.adminAppTitle')}
      breadcrumbs={breadcrumbs}
      paths={{
        logout: paths.auth.logout.getHref(),
        userProfile: paths.admin.profile.getHref(),
      }}
      appBarSx={{
        bgcolor: 'error.light',
      }}
      drawerSx={{
        '& .MuiDrawer-paper': {
          bgcolor: (theme: Theme) => alpha(theme.palette.error.main, 0.04),
        },
      }}
      appVersion={`${t('common.appVersion')}: ${env.APP_VERSION}`}
      backendVersion={`${t('common.backendVersion')}: ${env.BACKEND_VERSION}`}
    >
      {children}
    </DashboardLayout>
  );
};
